import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { isEmpty } from 'lodash';
import validate from 'validate.js';
import {
  ANALYST,
  COMPANY,
  COMPANY_USER,
  FUND,
  FUND_ADMIN,
  USER_ROLES,
} from 'common/constants/user';
import { ItemType } from 'pages/UserPermissions/common/types';
import { extractResponseErrorMessage } from 'utilities';
import {
  InviteUserLedgerFormState,
  NullableItemType,
  Role,
  UseInviteUserFormParams,
} from './InviteUserLedger.props';
import constraints from './InviteUserLedger.validations';

const DEFAULT_SUBMIT_ERROR = 'An error occurred while adding user to firm';

const getSubmitErrorMessage = (error: unknown): string =>
  extractResponseErrorMessage(error) ??
  (error instanceof Error ? error.message : null) ??
  DEFAULT_SUBMIT_ERROR;

export const useInviteUserForm = ({
  handleAddUserToFirm,
  isCompanyInvite,
  companyList,
  fundList,
}: UseInviteUserFormParams) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [roleType, setRoleType] = useState<number>(USER_ROLES[ANALYST].value);
  const [firmItem, setFirmItem] = useState<NullableItemType>({ itemType: null, itemId: null });

  const [formState, setFormState] = useState<InviteUserLedgerFormState>({
    isValid: false,
    values: { firstName: '', lastName: '', email: '', isPrimaryContact: false, sendActivationEmail: false },
    errors: {},
    touched: {},
  });

  const handleFieldChange = useCallback((name: string, value: string | boolean) => {
    setSubmitError(null);
    setFormState(prev => {
      const tmpValues = { ...prev.values, [name]: value };
      const errors = validate(tmpValues, constraints) || {};

      return {
        ...prev,
        values: tmpValues,
        errors,
        isValid: isEmpty(errors),
        touched: { ...prev.touched, [name]: true },
      };
    });
  }, []);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    handleFieldChange(name, type === 'checkbox' ? checked : value);
  };

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;
      handleFieldChange(name, checked);
    },
    [handleFieldChange],
  );

  const handleRoleToggle = useCallback((newRole: number) => {
    setSubmitError(null);
    setFirmItem({ itemType: null, itemId: null });
    setRoleType(newRole);
    setFormState(prev => ({
      ...prev,
      values: { ...prev.values, isPrimaryContact: false, sendActivationEmail: false },
    }));
  }, []);

  const handleSelect = useCallback(({ itemType, itemId }: ItemType) => {
    setSubmitError(null);
    if (itemId && itemType && [COMPANY, FUND].includes(itemType)) {
      setFirmItem({ itemType, itemId });
    }
  }, []);

  const companyUserRoleInvalid =
    roleType === USER_ROLES[COMPANY_USER].value && !isCompanyInvite && (companyList?.length === 0 || !firmItem.itemId);

  const fundAdminRoleInvalid =
    roleType === USER_ROLES[FUND_ADMIN].value && (fundList?.length === 0 || !firmItem.itemId);

  const isSubmitButtonDisabled =
    !formState.isValid || companyUserRoleInvalid || fundAdminRoleInvalid || isSubmitting;

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();
      setSubmitError(null);
      setIsSubmitting(true);

      const role: Role = { id: roleType, firmItem };

      try {
        await handleAddUserToFirm({ ...formState.values, role_id: roleType }, role);
      } catch (error) {
        setSubmitError(getSubmitErrorMessage(error));
      } finally {
        setIsSubmitting(false);
      }
    },
    [formState.values, roleType, firmItem, handleAddUserToFirm],
  );

  return {
    formState,
    roleType,
    firmItem,
    isSubmitting,
    submitError,
    isSubmitButtonDisabled,
    handleInputChange,
    handleCheckboxChange,
    handleRoleToggle,
    handleSelect,
    handleSubmit,
  };
};
